import { format, isValid } from 'date-fns';
import { useRef, useState } from 'react';

import { Button, DatePicker, Grid, TextField, Typography } from '@/ui';

import type { CoachCertification } from '@/types';
import type { FC } from 'react';

interface CertificationRow extends CoachCertification {
  rowId: number;
}

interface CertificationsProps {
  certifications: CoachCertification[];
  onChange: (certifications: CoachCertification[]) => void;
  onValidityChange?: (isValid: boolean) => void;
}

const parseDatePart = (value: string) => {
  const [year, month, day] = value.split('-').map(Number);
  if (!year || !month || !day) return null;
  const date = new Date(year, month - 1, day);
  return isValid(date) && format(date, 'yyyy-MM-dd') === value ? date : null;
};

const Certifications: FC<CertificationsProps> = ({
  certifications,
  onChange,
  onValidityChange,
}) => {
  const nextRowId = useRef(0);
  const getNextRowId = () => {
    const rowId = nextRowId.current;
    nextRowId.current += 1;
    return rowId;
  };
  const [dateErrors, setDateErrors] = useState<Record<number, boolean>>({});
  const [rows, setRows] = useState<CertificationRow[]>(() =>
    certifications.map((certification) => ({
      ...certification,
      rowId: getNextRowId(),
    })),
  );

  const updateRows = (updatedRows: CertificationRow[]) => {
    setRows(updatedRows);
    onChange(updatedRows.map(({ earnedOn, name }) => ({ earnedOn, name })));
  };

  const addCertification = () =>
    updateRows([...rows, { earnedOn: '', name: '', rowId: getNextRowId() }]);

  const updateCertification = (
    rowId: number,
    changes: Partial<CoachCertification>,
  ) =>
    updateRows(
      rows.map((row) => (row.rowId === rowId ? { ...row, ...changes } : row)),
    );

  const removeCertification = (rowId: number) => {
    const updatedErrors = Object.fromEntries(
      Object.entries(dateErrors).filter(([key]) => Number(key) !== rowId),
    );
    setDateErrors(updatedErrors);
    onValidityChange?.(!Object.values(updatedErrors).some(Boolean));
    updateRows(rows.filter((row) => row.rowId !== rowId));
  };

  const updateDateError = (rowId: number, hasError: boolean) => {
    const updatedErrors = { ...dateErrors, [rowId]: hasError };
    setDateErrors(updatedErrors);
    onValidityChange?.(!Object.values(updatedErrors).some(Boolean));
  };

  return (
    <Grid spacing={4} container>
      <Grid md={5} xs={12} item>
        <Typography className="mb-2" variant="h4">
          Certifications
        </Typography>
        <Typography variant="body1">
          Add professional certifications and the dates they were earned.
        </Typography>
      </Grid>
      <Grid className="mt-1 flex flex-col gap-4" md={7} xs={12} item>
        {rows.map((row) => (
          <div
            key={row.rowId}
            className="grid grid-cols-1 gap-3 rounded-xl border border-cement-300 p-4 md:grid-cols-8 md:items-start"
          >
            <TextField
              className="md:col-span-3"
              error={!row.name.trim()}
              helperText={!row.name.trim() ? 'Enter a certification name' : ''}
              label="Certification name"
              value={row.name}
              onChange={(event) =>
                updateCertification(row.rowId, { name: event.target.value })
              }
            />
            <DatePicker
              className="md:col-span-3"
              label="Earned date"
              slotProps={{
                textField: {
                  error: !row.earnedOn || dateErrors[row.rowId],
                  helperText:
                    !row.earnedOn || dateErrors[row.rowId]
                      ? 'Enter a valid earned date'
                      : '',
                },
              }}
              value={parseDatePart(row.earnedOn)}
              onChange={(date) => {
                if (!date) {
                  updateCertification(row.rowId, { earnedOn: '' });
                } else if (isValid(date)) {
                  updateCertification(row.rowId, {
                    earnedOn: format(date, 'yyyy-MM-dd'),
                  });
                }
              }}
              onError={(reason) => updateDateError(row.rowId, Boolean(reason))}
            />
            <Button
              aria-label="Remove certification"
              className="md:col-span-2"
              variant="outlined"
              onClick={() => removeCertification(row.rowId)}
            >
              Remove
            </Button>
          </div>
        ))}
        <Button variant="outlined" onClick={addCertification}>
          Add certification
        </Button>
      </Grid>
    </Grid>
  );
};

export default Certifications;
